/**
 * F4 (paridad Ledgeria) — Inmovilizado.
 *
 * Un activo es una compra que NO es gasto del año: va al balance y se lleva a
 * resultados por partes. La pantalla enseña las tres cosas que se preguntan de
 * verdad — cuánto costó, cuánto se ha amortizado ya y cuánto vale hoy — y deja
 * pasar la dotación del ejercicio con una simulación previa.
 */
import { useCallback, useEffect, useState } from 'react'
import { AlertCircle, Boxes, Calculator, Loader2, Plus, TrendingDown, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { api, ApiError } from '../lib/api'
import { confirmDialog } from '../lib/dialogs'
import { currentLocaleTag } from '../lib/formatting'
import { formatCents, parseEurosToCents, centsToEurosInput } from '../lib/money'
import Modal from '../components/ui/Modal'
import { useAuth } from '../contexts/AuthContext'

interface AssetDto {
  id: string
  code: string
  name: string
  accountCode: string
  accumulatedAccountCode: string
  depreciationAccountCode: string
  acquisitionDate: string
  acquisitionCostCents: number
  residualValueCents: number
  usefulLifeYears: number
  status: 'ACTIVO' | 'AMORTIZADO' | 'BAJA'
  disposalDate: string | null
  postedDepreciationCents: number
  theoreticalDepreciationCents: number
  pendingDepreciationCents: number
  netBookValueCents: number
}

interface ListResponse {
  items: AssetDto[]
  year: number
  totals: {
    costCents: number
    accumulatedCents: number
    netBookValueCents: number
    pendingCents: number
  }
}

interface SchedulePreview {
  dryRun: boolean
  year: number
  assetCount: number
  totalCents: number
  items: Array<{ assetId: string; code: string; name: string; amountCents: number }>
}

const STATUS_PILL: Record<AssetDto['status'], string> = {
  ACTIVO: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/40',
  AMORTIZADO: 'bg-sky-500/15 text-sky-700 dark:text-sky-300 border-sky-500/40',
  BAJA: 'bg-slate-500/15 text-slate-600 dark:text-slate-300 border-slate-500/30',
}

const inputCls = 'rounded-md border px-2 py-1.5 text-sm'
const inputStyle = { background: 'var(--bg-input)', borderColor: 'var(--border)' } as const

function errorText(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    return (err.body as { error?: string } | undefined)?.error ?? err.message
  }
  return err instanceof Error ? err.message : fallback
}

export default function FixedAssets() {
  const { t } = useTranslation(['assets', 'common'])
  const auth = useAuth()
  const canWrite = auth.status === 'authenticated' && auth.user.role === 'ADMIN'
  const currentYear = new Date().getFullYear()

  const [year, setYear] = useState(currentYear)
  const [data, setData] = useState<ListResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [scheduleFor, setScheduleFor] = useState<AssetDto | null>(null)
  const [depreciating, setDepreciating] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setData(await api.get<ListResponse>(`/fixed-assets?year=${year}`))
    } catch (err) {
      setError(errorText(err, t('errors.load')))
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [year, t])

  useEffect(() => {
    void load()
  }, [load])

  const onDelete = async (asset: AssetDto) => {
    if (!(await confirmDialog({ message: t('confirmDelete', { name: asset.name }), tone: 'danger' })))
      return
    try {
      await api.delete(`/fixed-assets/${asset.id}`)
      await load()
    } catch (err) {
      setError(errorText(err, t('errors.generic')))
    }
  }

  return (
    <div className="space-y-4 animate-slideUp">
      <header className="flex flex-wrap items-end gap-3 justify-between">
        <div>
          <h1 className="text-xl font-serif font-semibold t-primary flex items-center gap-2">
            <Boxes size={20} className="text-amber-600 dark:text-amber-300" />
            {t('title')}
          </h1>
          <p className="text-xs t-muted">{t('subtitle')}</p>
        </div>
        <div className="flex items-end gap-2">
          <label className="text-xs t-muted flex flex-col gap-1">
            {t('filters.year')}
            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className={inputCls}
              style={inputStyle}
            >
              {Array.from({ length: 8 }, (_, i) => currentYear + 1 - i).map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </label>
          {canWrite && (
            <>
              <button
                type="button"
                onClick={() => setDepreciating(true)}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-base text-sm hover:bg-hover t-primary"
              >
                <TrendingDown size={15} /> {t('depreciate.button', { year })}
              </button>
              <button
                type="button"
                onClick={() => setCreating(true)}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-toga-600 hover:bg-toga-500 text-white text-sm"
              >
                <Plus size={15} /> {t('newAsset')}
              </button>
            </>
          )}
        </div>
      </header>

      {error && (
        <div className="surface p-3 flex items-start gap-2 border-rose-500/40">
          <AlertCircle size={16} className="text-rose-400 mt-0.5 shrink-0" />
          <p className="text-sm text-rose-700 dark:text-rose-300">{error}</p>
        </div>
      )}

      {data && (
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Card label={t('totals.cost')} value={formatCents(data.totals.costCents)} />
          <Card label={t('totals.accumulated')} value={formatCents(data.totals.accumulatedCents)} />
          <Card label={t('totals.netBookValue')} value={formatCents(data.totals.netBookValueCents)} accent />
          <Card
            label={t('totals.pending', { year })}
            value={formatCents(data.totals.pendingCents)}
            hint={data.totals.pendingCents > 0 ? t('totals.pendingHint') : undefined}
          />
        </section>
      )}

      <section className="surface overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs t-muted uppercase tracking-wider" style={{ borderBottom: '1px solid var(--border)' }}>
              <th className="px-3 py-2">{t('columns.code')}</th>
              <th className="px-3 py-2">{t('columns.name')}</th>
              <th className="px-3 py-2">{t('columns.acquisition')}</th>
              <th className="px-3 py-2 text-right">{t('columns.cost')}</th>
              <th className="px-3 py-2 text-center">{t('columns.life')}</th>
              <th className="px-3 py-2 text-right">{t('columns.accumulated')}</th>
              <th className="px-3 py-2 text-right">{t('columns.netBookValue')}</th>
              <th className="px-3 py-2">{t('columns.status')}</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={9} className="px-3 py-6 text-center text-xs t-muted">
                  <Loader2 size={14} className="animate-spin inline mr-1.5" />
                  {t('loading')}
                </td>
              </tr>
            ) : (data?.items.length ?? 0) === 0 ? (
              <tr>
                <td colSpan={9} className="px-3 py-6 text-center text-xs t-muted">
                  {t('empty')}
                </td>
              </tr>
            ) : (
              data!.items.map((a) => (
                <tr key={a.id} style={{ borderBottom: '1px solid var(--border)' }} className="t-primary">
                  <td className="px-3 py-1.5 font-mono text-xs whitespace-nowrap">{a.code}</td>
                  <td className="px-3 py-1.5 max-w-xs truncate" title={a.name}>
                    {a.name}
                    <span className="t-faint text-[10px] ml-1.5 font-mono">{a.accountCode}</span>
                  </td>
                  <td className="px-3 py-1.5 text-xs whitespace-nowrap">
                    {new Date(a.acquisitionDate).toLocaleDateString(currentLocaleTag())}
                  </td>
                  <td className="px-3 py-1.5 text-right font-mono text-xs">
                    {formatCents(a.acquisitionCostCents)}
                  </td>
                  <td className="px-3 py-1.5 text-center text-xs t-muted">{a.usefulLifeYears}</td>
                  <td className="px-3 py-1.5 text-right font-mono text-xs">
                    {formatCents(a.postedDepreciationCents)}
                    {a.pendingDepreciationCents > 0 && (
                      <span className="text-amber-700 dark:text-amber-300 ml-1" title={t('totals.pendingHint')}>
                        +{formatCents(a.pendingDepreciationCents)}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-right font-mono text-xs font-semibold">
                    {formatCents(a.netBookValueCents)}
                  </td>
                  <td className="px-3 py-1.5">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border ${STATUS_PILL[a.status]}`}>
                      {t(`status.${a.status}`)}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 text-right whitespace-nowrap">
                    <button
                      type="button"
                      onClick={() => setScheduleFor(a)}
                      title={t('actions.schedule')}
                      className="p-1 rounded hover:bg-hover t-muted"
                    >
                      <Calculator size={13} />
                    </button>
                    {canWrite && a.postedDepreciationCents === 0 && a.status !== 'BAJA' && (
                      <button
                        type="button"
                        onClick={() => void onDelete(a)}
                        title={t('common:actions.delete')}
                        className="p-1 rounded hover:bg-hover text-rose-600 dark:text-rose-400"
                      >
                        <Trash2 size={13} />
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </section>

      {creating && (
        <NewAssetModal
          onClose={() => setCreating(false)}
          onCreated={async () => {
            setCreating(false)
            await load()
          }}
        />
      )}

      {scheduleFor && (
        <ScheduleModal
          asset={scheduleFor}
          canWrite={canWrite}
          onClose={() => setScheduleFor(null)}
          onDisposed={async () => {
            setScheduleFor(null)
            await load()
          }}
        />
      )}

      {depreciating && (
        <DepreciateModal
          year={year}
          onClose={() => setDepreciating(false)}
          onPosted={async () => {
            setDepreciating(false)
            await load()
          }}
        />
      )}
    </div>
  )
}

function Card({ label, value, hint, accent }: { label: string; value: string; hint?: string; accent?: boolean }) {
  return (
    <div className={`surface p-3 ${accent ? 'ring-1 ring-toga-500/30' : ''}`}>
      <div className="text-[10px] uppercase tracking-wider t-muted">{label}</div>
      <div className="text-lg font-semibold t-primary tabular-nums">{value}</div>
      {hint && <div className="text-[10px] text-amber-700 dark:text-amber-300 mt-0.5">{hint}</div>}
    </div>
  )
}

/** Pares cuenta-activo / amortización acumulada / dotación del PGC. */
const ACCOUNT_PRESETS = [
  { accountCode: '217', accumulatedAccountCode: '2817', depreciationAccountCode: '681' },
  { accountCode: '216', accumulatedAccountCode: '2816', depreciationAccountCode: '681' },
  { accountCode: '218', accumulatedAccountCode: '2818', depreciationAccountCode: '681' },
  { accountCode: '206', accumulatedAccountCode: '2806', depreciationAccountCode: '680' },
]

function NewAssetModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => Promise<void> | void }) {
  const { t } = useTranslation(['assets', 'common'])
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [preset, setPreset] = useState(0)
  const [acquisitionDate, setAcquisitionDate] = useState(new Date().toISOString().slice(0, 10))
  const [cost, setCost] = useState('')
  const [residual, setResidual] = useState('0')
  const [life, setLife] = useState('4')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async () => {
    setSaving(true)
    setError(null)
    try {
      await api.post('/fixed-assets', {
        code: code.trim(),
        name: name.trim(),
        ...ACCOUNT_PRESETS[preset],
        acquisitionDate,
        acquisitionCostCents: parseEurosToCents(cost),
        residualValueCents: parseEurosToCents(residual || '0'),
        usefulLifeYears: Number(life),
      })
      await onCreated()
    } catch (err) {
      setError(errorText(err, t('errors.generic')))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal open onClose={onClose} title={t('newAsset')} maxWidth="max-w-lg">
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs t-muted block mb-1">{t('fields.code')}</span>
            <input value={code} onChange={(e) => setCode(e.target.value)} className={`${inputCls} w-full font-mono`} style={inputStyle} />
          </label>
          <label className="block">
            <span className="text-xs t-muted block mb-1">{t('fields.acquisitionDate')}</span>
            <input type="date" value={acquisitionDate} onChange={(e) => setAcquisitionDate(e.target.value)} className={`${inputCls} w-full`} style={inputStyle} />
          </label>
        </div>
        <label className="block">
          <span className="text-xs t-muted block mb-1">{t('fields.name')}</span>
          <input value={name} onChange={(e) => setName(e.target.value)} className={`${inputCls} w-full`} style={inputStyle} />
        </label>
        <label className="block">
          <span className="text-xs t-muted block mb-1">{t('fields.accounts')}</span>
          <select value={preset} onChange={(e) => setPreset(Number(e.target.value))} className={`${inputCls} w-full`} style={inputStyle}>
            {ACCOUNT_PRESETS.map((p, i) => (
              <option key={p.accountCode} value={i}>
                {t(`presets.${p.accountCode}`)} · {p.accountCode} / {p.accumulatedAccountCode} / {p.depreciationAccountCode}
              </option>
            ))}
          </select>
        </label>
        <div className="grid grid-cols-3 gap-3">
          <label className="block">
            <span className="text-xs t-muted block mb-1">{t('fields.cost')}</span>
            <input value={cost} onChange={(e) => setCost(e.target.value)} placeholder="1.200,00" className={`${inputCls} w-full text-right font-mono`} style={inputStyle} />
          </label>
          <label className="block">
            <span className="text-xs t-muted block mb-1">{t('fields.residual')}</span>
            <input value={residual} onChange={(e) => setResidual(e.target.value)} className={`${inputCls} w-full text-right font-mono`} style={inputStyle} />
          </label>
          <label className="block">
            <span className="text-xs t-muted block mb-1">{t('fields.life')}</span>
            <input type="number" min={1} max={50} value={life} onChange={(e) => setLife(e.target.value)} className={`${inputCls} w-full text-right font-mono`} style={inputStyle} />
          </label>
        </div>

        {error && (
          <p className="text-xs text-rose-700 dark:text-rose-300 flex items-center gap-1.5">
            <AlertCircle size={12} /> {error}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="px-3 py-1.5 rounded-md text-sm t-muted hover:bg-hover">
            {t('common:actions.cancel')}
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={saving || !code.trim() || !name.trim() || !cost.trim()}
            className="px-3 py-1.5 rounded-md bg-toga-600 hover:bg-toga-500 text-white text-sm disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            {saving && <Loader2 size={13} className="animate-spin" />}
            {t('common:actions.save')}
          </button>
        </div>
      </div>
    </Modal>
  )
}

function ScheduleModal({
  asset,
  canWrite,
  onClose,
  onDisposed,
}: {
  asset: AssetDto
  canWrite: boolean
  onClose: () => void
  onDisposed: () => Promise<void> | void
}) {
  const { t } = useTranslation(['assets', 'common'])
  const [rows, setRows] = useState<Array<{
    year: number
    amountCents: number
    accumulatedCents: number
    netBookValueCents: number
    days: number
    postedCents: number | null
  }> | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [disposing, setDisposing] = useState(false)
  const [disposalDate, setDisposalDate] = useState(new Date().toISOString().slice(0, 10))
  const [disposalAmount, setDisposalAmount] = useState('0')

  useEffect(() => {
    api
      .get<{ rows: typeof rows }>(`/fixed-assets/${asset.id}/schedule`)
      .then((r) => setRows(r.rows))
      .catch((err) => setError(errorText(err, t('errors.load'))))
  }, [asset.id, t])

  const dispose = async () => {
    if (!(await confirmDialog({ message: t('dispose.confirm', { name: asset.name }), tone: 'danger' })))
      return
    setDisposing(true)
    setError(null)
    try {
      await api.post(`/fixed-assets/${asset.id}/dispose`, {
        disposalDate,
        disposalAmountCents: parseEurosToCents(disposalAmount || '0'),
        counterpartAccountCode: '572',
      })
      await onDisposed()
    } catch (err) {
      setError(errorText(err, t('errors.generic')))
    } finally {
      setDisposing(false)
    }
  }

  return (
    <Modal open onClose={onClose} title={`${asset.code} · ${asset.name}`} maxWidth="max-w-2xl">
      <div className="space-y-3">
        {error && (
          <p className="text-xs text-rose-700 dark:text-rose-300 flex items-center gap-1.5">
            <AlertCircle size={12} /> {error}
          </p>
        )}
        {!rows ? (
          <div className="t-faint text-sm flex items-center gap-1.5 py-6 justify-center">
            <Loader2 size={14} className="animate-spin" /> {t('loading')}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs t-muted uppercase tracking-wider" style={{ borderBottom: '1px solid var(--border)' }}>
                <th className="py-1.5">{t('schedule.year')}</th>
                <th className="py-1.5 text-center">{t('schedule.days')}</th>
                <th className="py-1.5 text-right">{t('schedule.amount')}</th>
                <th className="py-1.5 text-right">{t('schedule.accumulated')}</th>
                <th className="py-1.5 text-right">{t('schedule.netBookValue')}</th>
                <th className="py-1.5 text-right">{t('schedule.posted')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.year} style={{ borderBottom: '1px solid var(--border)' }} className="t-primary">
                  <td className="py-1.5 font-mono text-xs">{r.year}</td>
                  <td className="py-1.5 text-center text-xs t-muted">{r.days}</td>
                  <td className="py-1.5 text-right font-mono text-xs">{formatCents(r.amountCents)}</td>
                  <td className="py-1.5 text-right font-mono text-xs t-muted">{formatCents(r.accumulatedCents)}</td>
                  <td className="py-1.5 text-right font-mono text-xs">{formatCents(r.netBookValueCents)}</td>
                  <td className="py-1.5 text-right font-mono text-xs">
                    {r.postedCents != null ? (
                      <span className="text-emerald-700 dark:text-emerald-300">{formatCents(r.postedCents)}</span>
                    ) : (
                      <span className="t-faint">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {canWrite && asset.status !== 'BAJA' && (
          <div className="pt-2 space-y-2" style={{ borderTop: '1px solid var(--border)' }}>
            <h3 className="text-xs t-muted uppercase tracking-wider">{t('dispose.title')}</h3>
            <p className="text-[11px] t-faint">{t('dispose.hint')}</p>
            <div className="flex items-end gap-2 flex-wrap">
              <label className="block">
                <span className="text-xs t-muted block mb-1">{t('dispose.date')}</span>
                <input type="date" value={disposalDate} onChange={(e) => setDisposalDate(e.target.value)} className={inputCls} style={inputStyle} />
              </label>
              <label className="block">
                <span className="text-xs t-muted block mb-1">{t('dispose.amount')}</span>
                <input value={disposalAmount} onChange={(e) => setDisposalAmount(e.target.value)} className={`${inputCls} text-right font-mono w-32`} style={inputStyle} />
              </label>
              <button
                type="button"
                onClick={() => void dispose()}
                disabled={disposing}
                className="px-3 py-1.5 rounded-md text-sm border border-rose-500/40 bg-rose-500/10 text-rose-700 dark:text-rose-300 hover:bg-rose-500/15 disabled:opacity-50 inline-flex items-center gap-1.5"
              >
                {disposing && <Loader2 size={13} className="animate-spin" />}
                {t('dispose.button')}
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}

function DepreciateModal({
  year,
  onClose,
  onPosted,
}: {
  year: number
  onClose: () => void
  onPosted: () => Promise<void> | void
}) {
  const { t } = useTranslation(['assets', 'common'])
  const [preview, setPreview] = useState<SchedulePreview | null>(null)
  const [posting, setPosting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    api
      .post<SchedulePreview>('/fixed-assets/depreciate', { year, dryRun: true })
      .then(setPreview)
      .catch((err) => setError(errorText(err, t('errors.load'))))
  }, [year, t])

  const post = async () => {
    setPosting(true)
    setError(null)
    try {
      await api.post('/fixed-assets/depreciate', { year, dryRun: false })
      await onPosted()
    } catch (err) {
      setError(errorText(err, t('errors.generic')))
    } finally {
      setPosting(false)
    }
  }

  return (
    <Modal open onClose={onClose} title={t('depreciate.title', { year })} maxWidth="max-w-lg">
      <div className="space-y-3">
        <p className="text-xs t-muted">{t('depreciate.hint')}</p>
        {error && (
          <p className="text-xs text-rose-700 dark:text-rose-300 flex items-center gap-1.5">
            <AlertCircle size={12} /> {error}
          </p>
        )}
        {!preview ? (
          <div className="t-faint text-sm flex items-center gap-1.5 py-6 justify-center">
            <Loader2 size={14} className="animate-spin" /> {t('loading')}
          </div>
        ) : preview.items.length === 0 ? (
          <p className="text-sm t-muted py-3">{t('depreciate.nothing')}</p>
        ) : (
          <>
            <ul className="space-y-1 text-sm max-h-72 overflow-y-auto">
              {preview.items.map((i) => (
                <li key={i.assetId} className="flex items-center justify-between gap-3 px-2 py-1 rounded hover:bg-hover">
                  <span className="truncate t-primary">
                    <span className="font-mono text-xs t-muted mr-2">{i.code}</span>
                    {i.name}
                  </span>
                  <span className="font-mono text-xs shrink-0">{formatCents(i.amountCents)}</span>
                </li>
              ))}
            </ul>
            <div className="flex items-center justify-between font-semibold t-primary pt-2" style={{ borderTop: '1px solid var(--border)' }}>
              <span className="text-sm">{t('depreciate.total')}</span>
              <span className="font-mono text-sm">{formatCents(preview.totalCents)}</span>
            </div>
          </>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="px-3 py-1.5 rounded-md text-sm t-muted hover:bg-hover">
            {t('common:actions.cancel')}
          </button>
          <button
            type="button"
            onClick={() => void post()}
            disabled={posting || !preview || preview.items.length === 0}
            className="px-3 py-1.5 rounded-md bg-toga-600 hover:bg-toga-500 text-white text-sm disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            {posting && <Loader2 size={13} className="animate-spin" />}
            {t('depreciate.confirm')}
          </button>
        </div>
      </div>
    </Modal>
  )
}
